import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import "./UserProfilePage.css";
import { FaEdit, FaTimes } from "react-icons/fa";
import { IoBook } from "react-icons/io5";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";

const UserProfilePage = ({ theme = "light", setTheme }) => {

  const [user, setUser] = useState({ first_name: "", last_name: "" });
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);

  const [businessSummary, setBusinessSummary] = useState([]);
  const [summaryRange, setSummaryRange] = useState({
    start_date: "",
    end_date: "",
    previous_start_date: "",
    previous_end_date: "",
  });

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [showSummaryPopup, setShowSummaryPopup] = useState(false);
  const [activeIssueIndex, setActiveIssueIndex] = useState(0);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await api.get("/user/profile");
        setUser(response.data);
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setProfileError("Failed to fetch user profile. Please try again.");
      } finally {
        setLoadingProfile(false);
      }
    };

    const fetchBusinessSummary = async () => {
      try {
        const response = await api.get("/user/business-performance-summary");

        setBusinessSummary(
          Array.isArray(response.data?.data) ? response.data.data : []
        );

        setSummaryRange({
          start_date: response.data?.start_date || "",
          end_date: response.data?.end_date || "",
          previous_start_date: response.data?.previous_start_date || "",
          previous_end_date: response.data?.previous_end_date || "",
        });
      } catch (err) {
        console.error("Error fetching business performance summary:", err);
        setSummaryError("Failed to fetch business performance summary.");
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchUserProfile();
    fetchBusinessSummary();
  }, []);

  const formatMoney = (value) => {
    const num = parseFloat(value || 0);
    const sign = num < 0 ? "-" : "";
    return `${sign}${Math.abs(num).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    })}`;
  };

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) return "—";
    const d = new Date(`${date}T00:00:00`);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const hasBusinessData = (row) => {
    return Number(row.net_sales || 0) > 0;
  };

  const formatPercentOrNoData = (value, row) => {
    if (!hasBusinessData(row)) return "No data";
    return `${Number(value || 0).toFixed(2)}%`;
  };

  const getPercentClass = (value, row) => {
    if (!hasBusinessData(row)) return "user-pro-muted-cell";

    const num = Number(value || 0);

    if (num >= 30) return "user-pro-bad";
    if (num >= 20) return "user-pro-warning";
    return "user-pro-good";
  };

  const getIssueMessages = () => {
    const issues = [];

    businessSummary.forEach((row) => {
      if (!hasBusinessData(row)) return;

      if (row.food_cost_trend === "up") {
        issues.push(`${row.name}: Food cost % increased`);
      }

      if (row.payroll_trend === "up") {
        issues.push(`${row.name}: Payroll % increased`);
      }
    });

    return issues;
  };

  useEffect(() => {
    const issues = getIssueMessages();

    if (issues.length <= 1) {
      setActiveIssueIndex(0);
      return;
    }

    const timer = setInterval(() => {
      setActiveIssueIndex((prev) => (prev + 1) % issues.length);
    }, 3500);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessSummary]);

  const renderTrend = (trend, row) => {
    if (!hasBusinessData(row)) {
      return (
        <span className="user-pro-trend-neutral" title="No current sales data">
          —
        </span>
      );
    }

    if (trend === "up") {
      return (
        <span
          className="user-pro-trend-up"
          title="Higher than previous 2 weeks"
        >
          ▲
        </span>
      );
    }

    if (trend === "down") {
      return (
        <span
          className="user-pro-trend-down"
          title="Lower than previous 2 weeks"
        >
          ▼
        </span>
      );
    }

    return (
      <span className="user-pro-trend-neutral" title="Same as previous 2 weeks">
        —
      </span>
    );
  };

  const getDashboardInsight = () => {
    const issues = getIssueMessages();

    if (businessSummary.length === 0) {
      return "No business data found yet.";
    }

    const withData = businessSummary.filter((row) => hasBusinessData(row));

    if (withData.length === 0) {
      return "No recent sales data found for the last 14 days.";
    }

    if (issues.length > 0) {
      return issues[activeIssueIndex % issues.length];
    }

    return "All businesses are stable or improving compared to the previous 2 weeks.";
  };

  const getInsightClass = () => {
    const withData = businessSummary.filter((row) => hasBusinessData(row));
    if (withData.length === 0) return "user-pro-insight-card neutral";

    const hasIncrease = withData.some(
      (row) => row.food_cost_trend === "up" || row.payroll_trend === "up"
    );

    return hasIncrease
      ? "user-pro-insight-card warning"
      : "user-pro-insight-card good";
  };

  

  const closeSummaryPopup = () => {
    setShowSummaryPopup(false);
  };

  if (loadingProfile) return <p>Loading...</p>;
  if (profileError) return <p className="user-pro-error">{profileError}</p>;

  const currentInsight = getDashboardInsight();

  return (
    <div className="user-pro-container">
      <h2 className="user-pro-heading">
        Welcome, {user.first_name || "User"}!
      </h2>

      <div className="user-pro-main-layout">
        <div className="user-pro-left">
          <div className="user-pro-card user-pro-spotcheck-card">
            <div className="user-pro-card-header user-pro-card-header-spotcheck">
              <div>
                <h3>Your Business Performance</h3>
                <p className="user-pro-muted user-pro-date-line">
                  Last 14 days: {formatDate(summaryRange.start_date)} -{" "}
                  {formatDate(summaryRange.end_date)}
                </p>
              </div>

              <button
                type="button"
                className="user-pro-expand-btn"
                onClick={() => setShowSummaryPopup(true)}
                aria-label="Open full business performance table"
                title="Open full business performance table"
              >
                <FaArrowUpRightFromSquare />
              </button>
            </div>

            {loadingSummary ? (
              <p className="user-pro-muted">Loading business summary...</p>
            ) : summaryError ? (
              <p className="user-pro-error">{summaryError}</p>
            ) : businessSummary.length === 0 ? (
              <p className="user-pro-muted">No business summary found.</p>
            ) : (
              <>
                <div className={getInsightClass()}>
                  <div className="user-pro-insight-label">Quick Insight</div>
                  <div
                    key={currentInsight}
                    className="user-pro-insight-text user-pro-insight-motion"
                  >
                    {currentInsight}
                  </div>
                </div>

                <div className="user-pro-table-head-wrap">
  <table className="user-pro-table user-pro-table-compact">
    <thead>
      <tr>
        <th>Business</th>
        <th>COGS %</th>
        <th></th>
        <th>Payroll %</th>
        <th></th>
      </tr>
    </thead>
  </table>
</div>

<div className="user-pro-table-body-wrap">
  <table className="user-pro-table user-pro-table-compact">
    <tbody>
      {businessSummary.map((row) => (
        <tr key={row.business_id}>
          <td>
            <div className="user-pro-business-name">
              {row.name || "—"}
            </div>
            <div className="user-pro-source-line">
              Sales: {row.income_source || "—"} · Payroll:{" "}
              {row.payroll_source || "—"}
            </div>
          </td>

          <td className={getPercentClass(row.food_cost_percentage, row)}>
            {formatPercentOrNoData(row.food_cost_percentage, row)}
          </td>

          <td>{renderTrend(row.food_cost_trend, row)}</td>

          <td className={getPercentClass(row.payroll_percentage, row)}>
            {formatPercentOrNoData(row.payroll_percentage, row)}
          </td>

          <td>{renderTrend(row.payroll_trend, row)}</td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
              </>
            )}
          </div>
        </div>

        <div className="user-pro-right">
          <ul className="tiles-pro">
            <li className="tile-pro">
              <Link to="/dashboard/user/edit" className="user-pro-link">
                <FaEdit /> Edit Profile
              </Link>
            </li>

            <li className="tile-pro">
              <Link to="/dashboard/faqs" className="user-pro-link">
                <IoBook /> FAQs
              </Link>
            </li>

            <li className="tile-pro">
              <Link to="/dashboard/billing" className="user-pro-link">
                <FaFileInvoiceDollar /> Billing & Subscription
              </Link>
            </li>

            <li className="tile-pro">
              <Link to="/dashboard/add/user" className="user-pro-link">
                <FaFileInvoiceDollar /> Users / Access
              </Link>
            </li>

            <li className="tile-pro">
              <Link to="/dashboard/support" className="user-pro-link">
                <FaFileInvoiceDollar /> Support Request
              </Link>
            </li>
            <li className="tile-pro">
  <Link to="/dashboard/subway-spotcheck-all" className="user-pro-link">
    <FaFileInvoiceDollar /> Daily Report
  </Link>
</li>
          </ul>
        </div>
      </div>

      {showSummaryPopup && (
        <div
          className="user-pro-popup-overlay"
          onClick={closeSummaryPopup}
          role="presentation"
        >
          <div className="user-pro-popup" onClick={(e) => e.stopPropagation()}>
            <div className="user-pro-popup-header">
              <div>
                <h3>Business Performance Summary</h3>
                <p className="user-pro-muted">
                  Current: {formatDate(summaryRange.start_date)} -{" "}
                  {formatDate(summaryRange.end_date)}
                  <br />
                  Compared to: {formatDate(summaryRange.previous_start_date)} -{" "}
                  {formatDate(summaryRange.previous_end_date)}
                </p>
              </div>

              <button
                type="button"
                className="user-pro-popup-close"
                onClick={closeSummaryPopup}
                aria-label="Close popup"
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="user-pro-popup-body">
              {businessSummary.length === 0 ? (
                <p className="user-pro-muted">No business summary found.</p>
              ) : (
                <div className="user-pro-popup-table-wrap">
  <table className="user-pro-table user-pro-table-full">
    <colgroup>
      <col className="popup-col-business" />
      <col className="popup-col-sales" />
      <col className="popup-col-cogs" />
      <col className="popup-col-cogs-percent" />
      <col className="popup-col-payroll" />
      <col className="popup-col-hours" />
      <col className="popup-col-ot" />
      <col className="popup-col-payroll-percent" />
      <col className="popup-col-income-source" />
      <col className="popup-col-payroll-source" />
    </colgroup>

    <thead>
      <tr>
        <th>Business</th>
        <th>Net Sales</th>
        <th>Cost of Goods</th>
        <th>COGS %</th>
        <th>Total Payroll</th>
        <th>Total Hours</th>
        <th>OT Hours</th>
        <th>Payroll %</th>
        <th>Income Source</th>
        <th>Payroll Source</th>
      </tr>
    </thead>

    <tbody>
      {businessSummary.map((row) => (
        <tr key={`popup-${row.business_id}`}>
          <td>{row.name || "—"}</td>
          <td>{formatMoney(row.net_sales)}</td>
          <td>{formatMoney(row.food_cost)}</td>

          <td className={getPercentClass(row.food_cost_percentage, row)}>
            {formatPercentOrNoData(row.food_cost_percentage, row)}
          </td>

          <td>{formatMoney(row.total_payroll)}</td>
          <td>{formatNumber(row.total_hours)}</td>
          <td>{formatNumber(row.total_ot_hours)}</td>

          <td className={getPercentClass(row.payroll_percentage, row)}>
            {formatPercentOrNoData(row.payroll_percentage, row)}
          </td>

          <td>{row.income_source || "—"}</td>
          <td>{row.payroll_source || "—"}</td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;



// import React, { useEffect, useState } from "react";
// import { Link } from "react-router-dom";
// import api from "../services/api";
// import "./UserProfilePage.css";
// import { FaEdit, FaTimes } from "react-icons/fa";
// import { IoBook } from "react-icons/io5";
// import { FaFileInvoiceDollar } from "react-icons/fa";
// import { FaArrowUpRightFromSquare } from "react-icons/fa6";

// const UserProfilePage = ({ theme = "light", setTheme }) => {
//   const [user, setUser] = useState({ first_name: "", last_name: "" });
//   const [loadingProfile, setLoadingProfile] = useState(true);
//   const [profileError, setProfileError] = useState(null);

//   const [businessSummary, setBusinessSummary] = useState([]);
//   const [summaryRange, setSummaryRange] = useState({
//     start_date: "",
//     end_date: "",
//     previous_start_date: "",
//     previous_end_date: "",
//   });
//   const [loadingSummary, setLoadingSummary] = useState(true);
//   const [summaryError, setSummaryError] = useState(null);

//   const [showSummaryPopup, setShowSummaryPopup] = useState(false);

//   useEffect(() => {
//     const fetchUserProfile = async () => {
//       try {
//         const response = await api.get("/user/profile");
//         setUser(response.data);
//       } catch (err) {
//         console.error("Error fetching user profile:", err);
//         setProfileError("Failed to fetch user profile. Please try again.");
//       } finally {
//         setLoadingProfile(false);
//       }
//     };

//     const fetchBusinessSummary = async () => {
//       try {
//         const response = await api.get("/user/business-performance-summary");

//         setBusinessSummary(
//           Array.isArray(response.data?.data) ? response.data.data : []
//         );

//         setSummaryRange({
//           start_date: response.data?.start_date || "",
//           end_date: response.data?.end_date || "",
//           previous_start_date: response.data?.previous_start_date || "",
//           previous_end_date: response.data?.previous_end_date || "",
//         });
//       } catch (err) {
//         console.error("Error fetching business performance summary:", err);
//         setSummaryError("Failed to fetch business performance summary.");
//       } finally {
//         setLoadingSummary(false);
//       }
//     };

//     fetchUserProfile();
//     fetchBusinessSummary();
//   }, []);

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     const sign = num < 0 ? "-" : "";
//     return `${sign}${Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     })}`;
//   };

//   const formatNumber = (value) => {
//     return Number(value || 0).toLocaleString("en-US", {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     });
//   };

//   const formatPercent = (value) => {
//     return `${Number(value || 0).toFixed(2)}%`;
//   };

//   const formatDate = (date) => {
//     if (!date) return "—";
//     const d = new Date(`${date}T00:00:00`);
//     if (isNaN(d.getTime())) return "—";
//     return d.toLocaleDateString("en-US", {
//       month: "short",
//       day: "numeric",
//       year: "numeric",
//     });
//   };

//   const renderTrend = (trend) => {
//     if (trend === "up") {
//       return (
//         <span
//           style={{ color: "red", fontWeight: "700", fontSize: "18px" }}
//           title="Higher than previous 2 weeks"
//         >
//           ▲
//         </span>
//       );
//     }

//     if (trend === "down") {
//       return (
//         <span
//           style={{ color: "green", fontWeight: "700", fontSize: "18px" }}
//           title="Lower than previous 2 weeks"
//         >
//           ▼
//         </span>
//       );
//     }

//     return (
//       <span style={{ color: "#777" }} title="Same as previous 2 weeks">
//         —
//       </span>
//     );
//   };

//   const closeSummaryPopup = () => {
//     setShowSummaryPopup(false);
//   };

//   if (loadingProfile) return <p>Loading...</p>;
//   if (profileError) return <p className="user-pro-error">{profileError}</p>;

//   return (
//     <div className="user-pro-container">
//       <h2 className="user-pro-heading">
//         Welcome, {user.first_name || "User"}!
//       </h2>

//       <div className="user-pro-main-layout">
//         <div className="user-pro-left">
//           <div className="user-pro-card user-pro-spotcheck-card">
//             <div className="user-pro-card-header user-pro-card-header-spotcheck">
//               <div>
//                 <h3>Business Performance Summary</h3>
//                 <p className="user-pro-muted">
//                   {formatDate(summaryRange.start_date)} -{" "}
//                   {formatDate(summaryRange.end_date)}
//                 </p>
//               </div>

//               <button
//                 type="button"
//                 className="user-pro-expand-btn"
//                 onClick={() => setShowSummaryPopup(true)}
//                 aria-label="Open full business performance table"
//                 title="Open full business performance table"
//               >
//                 <FaArrowUpRightFromSquare />
//               </button>
//             </div>

//             {loadingSummary ? (
//               <p className="user-pro-muted">Loading business summary...</p>
//             ) : summaryError ? (
//               <p className="user-pro-error">{summaryError}</p>
//             ) : businessSummary.length === 0 ? (
//               <p className="user-pro-muted">No business summary found.</p>
//             ) : (
//               <div className="user-pro-table-wrap">
//                 <table className="user-pro-table user-pro-table-compact">
//                   <thead>
//                     <tr>
//                       <th>Business</th>
//                       <th>Cost of Goods %</th>
//                       <th>COGs Trend</th>
//                       <th>Payroll %</th>
//                       <th>Payroll Trend</th>
//                     </tr>
//                   </thead>

//                   <tbody>
//                     {businessSummary.map((row) => (
//                       <tr key={row.business_id}>
//                         <td>{row.name || "—"}</td>
//                         <td>{formatPercent(row.food_cost_percentage)}</td>
//                         <td>{renderTrend(row.food_cost_trend)}</td>
//                         <td>{formatPercent(row.payroll_percentage)}</td>
//                         <td>{renderTrend(row.payroll_trend)}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//           </div>
//         </div>

//         <div className="user-pro-right">
//           <ul className="tiles-pro">
//             <li className="tile-pro">
//               <Link to="/dashboard/user/edit" className="user-pro-link">
//                 <FaEdit /> Edit Profile
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/faqs" className="user-pro-link">
//                 <IoBook /> FAQs
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/billing" className="user-pro-link">
//                 <FaFileInvoiceDollar /> Billing & Subscription
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/add/user" className="user-pro-link">
//                 <FaFileInvoiceDollar /> Users / Access
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/support" className="user-pro-link">
//                 <FaFileInvoiceDollar /> Support Request
//               </Link>
//             </li>
//           </ul>
//         </div>
//       </div>

//       {showSummaryPopup && (
//         <div
//           className="user-pro-popup-overlay"
//           onClick={closeSummaryPopup}
//           role="presentation"
//         >
//           <div
//             className="user-pro-popup"
//             onClick={(e) => e.stopPropagation()}
//           >
//             <div className="user-pro-popup-header">
//               <div>
//                 <h3>Business Performance Summary</h3>
//                 <p className="user-pro-muted">
//                   Current: {formatDate(summaryRange.start_date)} -{" "}
//                   {formatDate(summaryRange.end_date)}
//                   <br />
//                   Compared to: {formatDate(summaryRange.previous_start_date)} -{" "}
//                   {formatDate(summaryRange.previous_end_date)}
//                 </p>
//               </div>

//               <button
//                 type="button"
//                 className="user-pro-popup-close"
//                 onClick={closeSummaryPopup}
//                 aria-label="Close popup"
//                 title="Close"
//               >
//                 <FaTimes />
//               </button>
//             </div>

//             <div className="user-pro-popup-body">
//               {loadingSummary ? (
//                 <p className="user-pro-muted">Loading business summary...</p>
//               ) : summaryError ? (
//                 <p className="user-pro-error">{summaryError}</p>
//               ) : businessSummary.length === 0 ? (
//                 <p className="user-pro-muted">No business summary found.</p>
//               ) : (
//                 <div className="user-pro-popup-table-wrap">
//                   <table className="user-pro-table user-pro-table-full">
//                     <thead>
//                       <tr>
//                         <th>Business</th>
//                         <th>Net Sales</th>
//                         <th>Cost of Goods</th>
//                         <th>Cost of Goods %</th>
//                         <th>COGs Trend</th>
//                         <th>Total Payroll</th>
//                         <th>Total Hours</th>
//                         <th>OT Hours</th>
//                         <th>Payroll %</th>
//                         <th>Payroll Trend</th>
//                         <th>Income Source</th>
//                         <th>Payroll Source</th>
//                       </tr>
//                     </thead>

//                     <tbody>
//                       {businessSummary.map((row) => (
//                         <tr key={`popup-${row.business_id}`}>
//                           <td>{row.name || "—"}</td>
//                           <td>{formatMoney(row.net_sales)}</td>
//                           <td>{formatMoney(row.food_cost)}</td>
//                           <td>{formatPercent(row.food_cost_percentage)}</td>
//                           <td>{renderTrend(row.food_cost_trend)}</td>
//                           <td>{formatMoney(row.total_payroll)}</td>
//                           <td>{formatNumber(row.total_hours)}</td>
//                           <td>{formatNumber(row.total_ot_hours)}</td>
//                           <td>{formatPercent(row.payroll_percentage)}</td>
//                           <td>{renderTrend(row.payroll_trend)}</td>
//                           <td>{row.income_source || "—"}</td>
//                           <td>{row.payroll_source || "—"}</td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default UserProfilePage;



// import React, { useEffect, useState } from "react";
// import { Link } from "react-router-dom";
// import api from "../services/api";
// import "./UserProfilePage.css";
// import { FaEdit, FaTimes } from "react-icons/fa";
// import { IoBook } from "react-icons/io5";
// import { FaFileInvoiceDollar } from "react-icons/fa";
// import { FaArrowUpRightFromSquare } from "react-icons/fa6";

// const UserProfilePage = ({ theme = "light", setTheme }) => {
//   const [user, setUser] = useState({ first_name: "", last_name: "" });
//   const [loadingProfile, setLoadingProfile] = useState(true);
//   const [profileError, setProfileError] = useState(null);

//   const [spotChecks, setSpotChecks] = useState([]);
//   const [loadingSpotChecks, setLoadingSpotChecks] = useState(true);
//   const [spotChecksError, setSpotChecksError] = useState(null);

//   const [showSpotCheckPopup, setShowSpotCheckPopup] = useState(false);

//   useEffect(() => {
//     const fetchUserProfile = async () => {
//       try {
//         const response = await api.get("/user/profile");
//         setUser(response.data);
//       } catch (err) {
//         console.error("Error fetching user profile:", err);
//         setProfileError("Failed to fetch user profile. Please try again.");
//       } finally {
//         setLoadingProfile(false);
//       }
//     };

//     const fetchLatestSpotChecks = async () => {
//       try {
//         const response = await api.get("/user/latest-spot-checks");
//         setSpotChecks(Array.isArray(response.data) ? response.data : []);
//       } catch (err) {
//         console.error("Error fetching latest spot checks:", err);
//         setSpotChecksError("Failed to fetch latest spot checks.");
//       } finally {
//         setLoadingSpotChecks(false);
//       }
//     };

//     fetchUserProfile();
//     fetchLatestSpotChecks();
//   }, []);

//   // const handleTheme = (value) => {
//   //   if (setTheme) setTheme(value);
//   // };

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     const sign = num < 0 ? "-" : "";
//     return `${sign}${Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     })}`;
//   };

//   const formatDate = (date) => {
//     if (!date) return "—";
//     const d = new Date(`${date}T00:00:00`);
//     if (isNaN(d.getTime())) return "—";
//     return d.toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "short",
//       day: "numeric",
//     });
//   };

//   const closeSpotCheckPopup = () => {
//     setShowSpotCheckPopup(false);
//   };

//   if (loadingProfile) return <p>Loading...</p>;
//   if (profileError) return <p className="user-pro-error">{profileError}</p>;

//   return (
//     <div className="user-pro-container">
//       <h2 className="user-pro-heading">
//         Welcome, {user.first_name || "User"}!
//       </h2>

//       <div className="user-pro-main-layout">
//         <div className="user-pro-left">
//           <div className="user-pro-card user-pro-spotcheck-card">
//             <div className="user-pro-card-header user-pro-card-header-spotcheck">
//               <h3>Latest Spot Check by Business</h3>

//               <button
//                 type="button"
//                 className="user-pro-expand-btn"
//                 onClick={() => setShowSpotCheckPopup(true)}
//                 aria-label="Open full spot check table"
//                 title="Open full spot check table"
//               >
//                 <FaArrowUpRightFromSquare />
//               </button>
//             </div>

//             {loadingSpotChecks ? (
//               <p className="user-pro-muted">Loading latest spot checks...</p>
//             ) : spotChecksError ? (
//               <p className="user-pro-error">{spotChecksError}</p>
//             ) : spotChecks.length === 0 ? (
//               <p className="user-pro-muted">No spot checks found.</p>
//             ) : (
//               <div className="user-pro-table-wrap">
//                 <table className="user-pro-table user-pro-table-compact">
//                   <thead>
//                     <tr>
//                       <th>Business</th>
//                       <th>Date</th>
//                       <th>Profit / Loss</th>
//                       <th>View</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {spotChecks.map((row) => (
//                       <tr key={row.business_id}>
//                         <td>{row.name || "—"}</td>
//                         <td>{formatDate(row.log_date)}</td>
//                         <td
//                           className={
//                             row.log_id
//                               ? Number(row.profit_loss_total) < 0
//                                 ? "user-pro-negative"
//                                 : "user-pro-positive"
//                               : ""
//                           }
//                         >
//                           {row.log_id ? formatMoney(row.profit_loss_total) : "No log yet"}
//                         </td>
//                         <td>
//                           <Link
//                             to={`/dashboard/business/${row.business_id}/average-cogs`}
//                             className="user-pro-link"
//                           >
//                             View
//                           </Link>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//           </div>
//         </div>

//         <div className="user-pro-right">
//           <ul className="tiles-pro">
//             <li className="tile-pro">
//               <Link to="/dashboard/user/edit" className="user-pro-link">
//                 <FaEdit /> Edit Profile
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/faqs" className="user-pro-link">
//                 <IoBook /> FAQs
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/billing" className="user-pro-link">
//                 <FaFileInvoiceDollar /> Billing & Subscription
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/add/user" className="user-pro-link">
//                 <FaFileInvoiceDollar /> Users / Access
//               </Link>
//             </li>

//             <li className="tile-pro">
//               <Link to="/dashboard/support" className="user-pro-link">
//                 <FaFileInvoiceDollar /> Support Request
//               </Link>
//             </li>

//             {/* <li className="tile-pro">
//                <div className="theme-row">
//                 <label className={`theme-pill ${theme === "light" ? "active" : ""}`}>
//                   <input
//                     type="radio"
//                     name="theme"
//                     value="light"
//                     checked={theme === "light"}
//                     onChange={() => handleTheme("light")}
//                   />
//                   Light
//                 </label>

//                 <label className={`theme-pill ${theme === "dark" ? "active" : ""}`}>
//                   <input
//                     type="radio"
//                     name="theme"
//                     value="dark"
//                     checked={theme === "dark"}
//                     onChange={() => handleTheme("dark")}
//                   />
//                   Dark
//                 </label>
//               </div> 
//             </li> */}
//           </ul>
//         </div>
//       </div>

//       {showSpotCheckPopup && (
//         <div
//           className="user-pro-popup-overlay"
//           onClick={closeSpotCheckPopup}
//           role="presentation"
//         >
//           <div
//             className="user-pro-popup"
//             onClick={(e) => e.stopPropagation()}
//           >
//             <div className="user-pro-popup-header">
//               <h3>All Spot Check Columns</h3>

//               <button
//                 type="button"
//                 className="user-pro-popup-close"
//                 onClick={closeSpotCheckPopup}
//                 aria-label="Close popup"
//                 title="Close"
//               >
//                 <FaTimes />
//               </button>
//             </div>

//             <div className="user-pro-popup-body">
//               {loadingSpotChecks ? (
//                 <p className="user-pro-muted">Loading latest spot checks...</p>
//               ) : spotChecksError ? (
//                 <p className="user-pro-error">{spotChecksError}</p>
//               ) : spotChecks.length === 0 ? (
//                 <p className="user-pro-muted">No spot checks found.</p>
//               ) : (
//                 <div className="user-pro-popup-table-wrap">
//                   <table className="user-pro-table user-pro-table-full">
//                     <thead>
//                       <tr>
//                         <th>Business</th>
//                         <th>Date</th>
//                         <th>Net Sales</th>
//                         <th>COGS</th>
//                         <th>Payroll</th>
//                         <th>Payroll Tax</th>
//                         <th>Fixed Costs</th>
//                         <th>Profit / Loss</th>
//                         <th>View</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {spotChecks.map((row) => (
//                         <tr key={`popup-${row.business_id}`}>
//                           <td>{row.name || "—"}</td>
//                           <td>{formatDate(row.log_date)}</td>
//                           <td>{row.log_id ? formatMoney(row.net_sales) : "No log yet"}</td>
//                           <td>{row.log_id ? formatMoney(row.cogs_amount) : "—"}</td>
//                           <td>{row.log_id ? formatMoney(row.payroll_amount) : "—"}</td>
//                           <td>{row.log_id ? formatMoney(row.payroll_tax_amount) : "—"}</td>
//                           <td>{row.log_id ? formatMoney(row.fixed_cost_total) : "—"}</td>
//                           <td
//                             className={
//                               row.log_id
//                                 ? Number(row.profit_loss_total) < 0
//                                   ? "user-pro-negative"
//                                   : "user-pro-positive"
//                                 : ""
//                             }
//                           >
//                             {row.log_id ? formatMoney(row.profit_loss_total) : "—"}
//                           </td>
//                           <td>
//                             <Link
//                               to={`/dashboard/business/${row.business_id}/average-cogs`}
//                               className="user-pro-link"
//                               onClick={closeSpotCheckPopup}
//                             >
//                               View
//                             </Link>
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default UserProfilePage;

